import { useMemo, useState } from 'react';
import SiteSelector from './SiteSelector';
import ModuleIcon from './ModuleIcon';
import { moduleColor, orderModules } from './modulePalette';
import { IconClose } from './icons';

function langFlagEmoji(code) {
  const c = String(code || '').toLowerCase();
  if (c === 'fr') return '🇫🇷';
  if (c === 'en') return '🇬🇧';
  if (c === 'en-us') return '🇺🇸';
  if (c === 'es') return '🇪🇸';
  if (c === 'de') return '🇩🇪';
  if (c === 'it') return '🇮🇹';
  if (c === 'nl') return '🇳🇱';
  if (c === 'pt') return '🇵🇹';
  if (c === 'pt-br') return '🇧🇷';
  return '🏳️';
}

function NavItem({ active, disabled, right, accent, strong, children, onClick }) {
  return (
    <button
      type="button"
      className={`NavItem ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''} ${strong ? 'NavItem--strong' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled ? 'true' : 'false'}
      style={accent ? { '--accent': accent } : undefined}
    >
      <span className="NavLabel">
        <span className="NavIcon" aria-hidden="true">
          <ModuleIcon moduleKey={String(children?.key || '')} />
        </span>
        <span>{children?.label || children}</span>
      </span>
      {right ? <span>{right}</span> : null}
    </button>
  );
}

export default function Sidebar({
  user,
  sites,
  selectedSiteId,
  onSelectSite,
  languages,
  selectedLang,
  onSelectLang,
  modules,
  moduleNotifications,
  route,
  onNavigate,
  onLogout,
  onClose,
}) {
  const [accountOpen, setAccountOpen] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isSuperadmin = isAdmin && Boolean(user?.is_superadmin);
  const adminKeys = new Set(['clients', 'sites', 'logs', 'admin_settings']);
  const siteModules = (modules || []).filter((m) => m.key !== 'dashboard' && !adminKeys.has(m.key));
  const orderedSiteModules = orderModules(siteModules);
  const adminModules = (modules || []).filter((m) => adminKeys.has(m.key));
  const showLang = useMemo(() => Array.isArray(languages) && languages.filter((l) => l.is_enabled).length > 1, [languages]);

  return (
    <aside className="Sidebar" aria-label="Navigation principale">
      <button type="button" className="Button Button--ghost Button--icon SidebarClose" onClick={onClose} aria-label="Fermer le menu">
        <IconClose />
      </button>
      <img src="/omniscope_panel.png" alt="Omniscope" className="BrandLogo" />

      <div className="SidebarSection">
        <div className="SidebarAccountWrap">
          <button type="button" className="SidebarAccount" onClick={() => setAccountOpen((v) => !v)}>
            <div className="SidebarAccountName">{user?.display_name || user?.email}</div>
            <div className="SidebarAccountMeta">{isSuperadmin ? 'Administrateur' : isAdmin ? 'Employé' : 'Client'}</div>
          </button>
          {accountOpen ? (
            <>
              <div className="SidebarAccountFloating">
                <button type="button" className="Button Button--ghost" onClick={() => onNavigate?.('/account')}>
                  Paramètres du compte
                </button>
                <button type="button" className="Button Button--danger" onClick={onLogout}>
                  Déconnexion
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="SidebarSection">
        <div className="SidebarLabel">Site</div>
        <div className="SidebarSiteStack">
          <SiteSelector sites={sites} selectedSiteId={selectedSiteId} onChange={onSelectSite} variant="sidebar" />
          {showLang ? (
            <select className="Select Select--lang" value={selectedLang || ''} onChange={(e) => onSelectLang?.(e.target.value)}>
              {languages
                .filter((l) => l.is_enabled)
                .map((l) => (
                  <option key={l.lang_code} value={l.lang_code}>
                    {langFlagEmoji(l.lang_code)} {l.label} ({l.lang_code})
                  </option>
                ))}
            </select>
          ) : null}
        </div>
      </div>

      <div className="SidebarSection">
        <nav className="Nav" aria-label="Accueil">
          <NavItem
            strong
            active={route === '/' || route === '/dashboard'}
            accent={moduleColor('dashboard')}
            onClick={() => onNavigate('/dashboard', { reselect: route === '/dashboard' || route === '/' })}
          >
            {{ key: 'dashboard', label: 'Dashboard' }}
          </NavItem>
        </nav>
      </div>

      <div className="SidebarSection">
        <div className="SidebarLabel">Modules</div>
        <nav className="Nav" aria-label="Modules du site">
          {orderedSiteModules.map((m) => {
            const comingSoon = Boolean(m.coming_soon) && String(m.key) !== 'settings';
            const n = Number(moduleNotifications?.[String(m.key || '')] || 0);
            const notif = Number.isFinite(n) && n > 0 ? Math.min(99, n) : 0;
            const right = comingSoon ? (
              <span className="Badge">à venir</span>
            ) : notif > 0 ? (
              <span className="NavNotif" title={`${n} notification${n > 1 ? 's' : ''}`}>
                {n > 99 ? '99+' : String(n)}
              </span>
            ) : null;
            return (
              <NavItem
                key={m.key}
                active={route === m.route}
                disabled={comingSoon}
                accent={moduleColor(m.key, m.color)}
                right={right}
                onClick={() => onNavigate(m.route, { reselect: route === m.route })}
              >
                {{ key: m.key, label: m.label }}
              </NavItem>
            );
          })}
        </nav>
      </div>

      {isSuperadmin ? (
        <div className="SidebarSection SidebarSection--admin">
          <div className="SidebarLabel">Administration</div>
          <nav className="Nav" aria-label="Outils d’administration">
            {adminModules.map((m) => (
              <NavItem
                key={m.key}
                active={route === m.route}
                disabled={m.coming_soon}
                accent={m.color}
                right={m.coming_soon ? <span className="Badge">à venir</span> : null}
                onClick={() => onNavigate(m.route, { reselect: route === m.route })}
              >
                {{ key: m.key, label: m.label }}
              </NavItem>
            ))}

            <NavItem
              active={route === '/users'}
              accent={moduleColor('employees')}
              onClick={() => onNavigate('/users', { reselect: route === '/users' })}
            >
              {{ key: 'employees', label: 'Employés' }}
            </NavItem>
          </nav>
        </div>
      ) : null}
    </aside>
  );
}
